// Import Branch Actuals — bulk-load recorded branch-level actuals from CSV
// (paste or file). Columns: Branch (name or code), Hub (name or code), Date
// (MM-DD-YYYY or YYYY-MM-DD), Volume. Rows are matched to branches within their
// hub, validated, previewed, then upserted into branch_actuals. These feed the
// data-driven branch shares on the Branch Forecast page.

import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, Textarea } from "../../components/ui";
import { getErrorMessage } from "../../lib/errors";
import { fmtDate, fmtN } from "../../lib/format";
import type { Branch, Hub } from "../../lib/types";
import { listHubs } from "../hubs/hubsApi";
import { listBranches } from "../branches/branchesApi";
import { deleteAllBranchActuals, saveBranchActuals, type BranchActual } from "./branchActualsApi";

interface ParsedRow {
  line: number;
  branch: string;
  hub: string;
  date: string;
  branchId?: string;
  hubId?: string;
  isoDate?: string;
  vol?: number;
  error?: string;
}

const TEMPLATE =
  "Branch,Hub,Date,Volume\n" +
  "Florida,Brooklyn Depot,06-15-2026,3000\n" +
  "Tampa Branch,NYSC,06-15-2026,1200";

const num = (s: string) => Number((s ?? "").replace(/[, ]/g, ""));

function normalizeDate(s: string): string | null {
  const t = s.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const m = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) return `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
  return null;
}

export default function BranchActualsImportPage() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [hubs, setHubs] = useState<Hub[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState<ParsedRow[] | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!accountId) return;
    Promise.all([listHubs(accountId), listBranches(accountId)])
      .then(([h, b]) => {
        setHubs(h);
        setBranches(b);
      })
      .catch((e) => setError(getErrorMessage(e, "Failed to load hubs/branches.")));
  }, [accountId]);

  const hubLookup = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of hubs) {
      m.set(h.name.toLowerCase(), h.id);
      if (h.code) m.set(h.code.toLowerCase(), h.id);
    }
    return m;
  }, [hubs]);
  // Branch lookup keyed by `${hubId}|${name|code}` so codes are unique within a hub.
  const branchLookup = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of branches) {
      m.set(`${b.hubId}|${b.name.toLowerCase()}`, b.id);
      if (b.code) m.set(`${b.hubId}|${b.code.toLowerCase()}`, b.id);
    }
    return m;
  }, [branches]);

  function parse() {
    setMsg(null);
    setError(null);
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l !== "");
    const out: ParsedRow[] = [];
    lines.forEach((raw, i) => {
      const cells = raw.split(",").map((c) => c.trim());
      if (i === 0 && cells[3] !== undefined && Number.isNaN(num(cells[3]))) return; // header
      const [branch = "", hub = "", date = ""] = cells;
      const row: ParsedRow = { line: i + 1, branch, hub, date };
      row.hubId = hubLookup.get(hub.toLowerCase());
      row.branchId = row.hubId ? branchLookup.get(`${row.hubId}|${branch.toLowerCase()}`) : undefined;
      row.isoDate = normalizeDate(date) ?? undefined;
      const v = num(cells[3]);
      if (Number.isFinite(v) && v >= 0) row.vol = Math.round(v);

      if (!row.hubId) row.error = `Unknown hub "${hub}"`;
      else if (!row.branchId) row.error = `Unknown branch "${branch}" in ${hub}`;
      else if (!row.isoDate) row.error = `Bad date "${date}"`;
      else if (row.vol == null) row.error = "Bad / missing volume";
      out.push(row);
    });
    setParsed(out);
  }

  const valid = useMemo(() => (parsed ?? []).filter((r) => !r.error), [parsed]);
  const invalid = useMemo(() => (parsed ?? []).filter((r) => r.error), [parsed]);

  async function importRows() {
    if (!accountId || valid.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const entries: BranchActual[] = valid.map((r) => ({
        branchId: r.branchId!,
        hubId: r.hubId!,
        date: r.isoDate!,
        actualVolume: r.vol!,
      }));
      await saveBranchActuals(accountId, entries);
      setMsg(`Imported ${entries.length} branch actual${entries.length === 1 ? "" : "s"}.`);
      setParsed(null);
      setText("");
    } catch (e) {
      setError(getErrorMessage(e, "Import failed."));
    } finally {
      setBusy(false);
    }
  }

  async function clearAll() {
    if (!accountId) return;
    if (!confirm("Delete ALL branch actuals for this account? This cannot be undone.")) return;
    setBusy(true);
    setError(null);
    try {
      await deleteAllBranchActuals(accountId);
      setMsg("All branch actuals deleted.");
    } catch (e) {
      setError(getErrorMessage(e, "Delete failed."));
    } finally {
      setBusy(false);
    }
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result ?? ""));
    reader.readAsText(file);
  }

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold tracking-tight text-slate-900">Import Branch Actuals</h1>
        {msg && <span className="text-sm text-emerald-600">{msg}</span>}
      </div>
      <p className="text-xs text-slate-500">
        Paste or upload CSV: <span className="font-medium">Branch, Hub, Date, Volume</span>. Branch matches by name or
        code <i>within its hub</i>; Hub matches by name or code; Date is MM-DD-YYYY or YYYY-MM-DD. Existing rows for the
        same branch/date are overwritten. These feed the <b>data-driven shares</b> on the Branch Forecast page.
      </p>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>
      )}

      <Card className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" onChange={onFile} className="hidden" />
          <Button variant="ghost" type="button" onClick={() => fileRef.current?.click()}>
            Upload CSV
          </Button>
          {fileName && <span className="text-xs text-slate-500">{fileName}</span>}
          <span className="text-xs text-slate-400">or paste below ·</span>
          <button type="button" onClick={() => setText(TEMPLATE)} className="text-xs font-medium text-brand-600 hover:underline">
            Insert template
          </button>
        </div>
        <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder={TEMPLATE} className="font-mono text-xs" />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button onClick={parse} disabled={!text.trim()}>
              Parse
            </Button>
            {valid.length > 0 && (
              <Button onClick={importRows} disabled={busy}>
                {busy ? "…" : `Import ${valid.length} valid row${valid.length === 1 ? "" : "s"}`}
              </Button>
            )}
          </div>
          <Button variant="danger" onClick={clearAll} disabled={busy}>
            Delete all branch actuals
          </Button>
        </div>
      </Card>

      {parsed && (
        <Card className="overflow-x-auto p-0">
          <div className="border-b border-slate-200 px-5 py-3 text-sm font-medium text-slate-700">
            Preview — {valid.length} valid, {invalid.length} with errors
          </div>
          {parsed.length === 0 ? (
            <p className="p-5 text-sm text-slate-500">Nothing parsed. Check the format.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-2 font-medium">Branch</th>
                  <th className="px-3 py-2 font-medium">Hub</th>
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 text-right font-medium">Volume</th>
                  <th className="px-5 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {parsed.slice(0, 300).map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-1.5 text-slate-700">{r.branch}</td>
                    <td className="px-3 py-1.5 text-slate-600">{r.hub}</td>
                    <td className="px-3 py-1.5 text-slate-600">{r.isoDate ? fmtDate(r.isoDate) : r.date}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium text-slate-800">{r.vol == null ? "—" : fmtN(r.vol)}</td>
                    <td className="px-5 py-1.5">{r.error ? <span className="text-red-600">{r.error}</span> : <span className="text-emerald-600">ok</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {parsed.length > 300 && <p className="px-5 py-2 text-xs text-slate-400">Showing first 300 of {parsed.length} rows.</p>}
        </Card>
      )}
    </div>
  );
}
